/**
 * Tabs block — tab switching + accordion behaviour.
 *
 * Desktop (≥ 768px): Tab order is tab → that tab's content → next tab → …
 * Mobile / tablet (< 768px): each panel has an embedded accordion trigger.
 *
 * The two modes share the same markup; CSS hides/shows the respective
 * controls at each breakpoint, and this script responds to both.
 */

const BREAKPOINT = 768;

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join( ', ' );

/**
 * Initialise all .block-tabs elements on the page.
 */
export function initTabs() {
	document.querySelectorAll( '[data-block="tabs"]' ).forEach( initTabBlock );
}

/**
 * Focusable elements inside a panel (excludes decorative accordion heading).
 *
 * @param {HTMLElement|null} panel
 * @returns {HTMLElement[]}
 */
function getPanelFocusables( panel ) {
	if ( ! panel ) {
		return [];
	}

	return Array.from( panel.querySelectorAll( FOCUSABLE_SELECTOR ) ).filter( ( el ) => {
		if ( el.classList.contains( 'block-tabs__accordion-btn' ) ) {
			return false;
		}
		if ( el.hasAttribute( 'disabled' ) || el.getAttribute( 'aria-hidden' ) === 'true' ) {
			return false;
		}
		const style = window.getComputedStyle( el );
		if ( style.display === 'none' || style.visibility === 'hidden' ) {
			return false;
		}
		return true;
	} );
}

/**
 * Wire up a single tabs block.
 *
 * @param {HTMLElement} block
 */
function initTabBlock( block ) {
	const navBtns = Array.from( block.querySelectorAll( '.block-tabs__nav-btn' ) );
	const panels = Array.from( block.querySelectorAll( '.block-tabs__panel' ) );
	const accordionBtns = Array.from( block.querySelectorAll( '.block-tabs__accordion-btn' ) );

	if ( ! panels.length ) {
		return;
	}

	const isDesktop = () => window.matchMedia( `(min-width: ${ BREAKPOINT }px)` ).matches;

	/**
	 * Desktop: nav tabs stay in tab order.
	 * Mobile: accordion buttons are tab stops; nav buttons get tabindex="-1".
	 */
	function syncViewportTabStops() {
		if ( isDesktop() ) {
			navBtns.forEach( ( btn ) => {
				btn.setAttribute( 'tabindex', '0' );
			} );
			accordionBtns.forEach( ( btn ) => {
				btn.setAttribute( 'tabindex', '-1' );
			} );
			return;
		}

		navBtns.forEach( ( btn ) => {
			btn.setAttribute( 'tabindex', '-1' );
		} );
		accordionBtns.forEach( ( btn ) => {
			btn.removeAttribute( 'tabindex' );
		} );
	}

	/**
	 * Activate a tab by index, handling both desktop and mobile states.
	 *
	 * @param {number}  index        The tab index to activate.
	 * @param {boolean} toggleMobile On mobile, true = toggle; false = always open.
	 */
	function activate( index, toggleMobile = false ) {
		if ( isDesktop() ) {
			navBtns.forEach( ( btn, i ) => {
				const active = i === index;
				btn.classList.toggle( 'is-active', active );
				btn.setAttribute( 'aria-selected', active ? 'true' : 'false' );
			} );

			panels.forEach( ( panel, i ) => {
				const active = i === index;
				panel.classList.toggle( 'is-active', active );
				if ( active ) {
					panel.removeAttribute( 'hidden' );
				} else {
					panel.setAttribute( 'hidden', '' );
				}
			} );

			accordionBtns.forEach( ( btn, i ) => {
				const active = i === index;
				btn.classList.toggle( 'is-expanded', active );
				btn.setAttribute( 'aria-expanded', active ? 'true' : 'false' );
			} );
		} else {
			panels.forEach( ( panel ) => panel.removeAttribute( 'hidden' ) );

			const targetPanel = panels[ index ];
			const targetBtn = accordionBtns[ index ];

			if ( targetPanel && targetBtn ) {
				const isCurrentlyOpen = targetPanel.classList.contains( 'is-active' );

				if ( toggleMobile && isCurrentlyOpen ) {
					targetPanel.classList.remove( 'is-active' );
					targetBtn.classList.remove( 'is-expanded' );
					targetBtn.setAttribute( 'aria-expanded', 'false' );
				} else {
					panels.forEach( ( panel, i ) => {
						panel.classList.remove( 'is-active' );
						if ( accordionBtns[ i ] ) {
							accordionBtns[ i ].classList.remove( 'is-expanded' );
							accordionBtns[ i ].setAttribute( 'aria-expanded', 'false' );
						}
					} );
					targetPanel.classList.add( 'is-active' );
					targetBtn.classList.add( 'is-expanded' );
					targetBtn.setAttribute( 'aria-expanded', 'true' );
				}
			}
		}

		syncViewportTabStops();
	}

	/**
	 * Move focus into a panel's first control, or skip to the next tab if none.
	 *
	 * @param {number} index
	 */
	function focusPanelStart( index ) {
		activate( index );
		window.requestAnimationFrame( () => {
			const focusables = getPanelFocusables( panels[ index ] );
			if ( focusables.length > 0 ) {
				focusables[ 0 ].focus();
				return;
			}
			if ( index + 1 < navBtns.length ) {
				activate( index + 1 );
				navBtns[ index + 1 ].focus();
			}
		} );
	}

	/**
	 * Move focus to a panel's last control (for Shift+Tab), or its tab button.
	 *
	 * @param {number} index
	 */
	function focusPanelEnd( index ) {
		activate( index );
		window.requestAnimationFrame( () => {
			const focusables = getPanelFocusables( panels[ index ] );
			if ( focusables.length > 0 ) {
				focusables[ focusables.length - 1 ].focus();
				return;
			}
			navBtns[ index ].focus();
		} );
	}

	navBtns.forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const index = parseInt( btn.dataset.tabIndex, 10 );
			activate( index );
			btn.focus();
		} );

		btn.addEventListener( 'keydown', ( e ) => {
			const index = parseInt( btn.dataset.tabIndex, 10 );

			if ( e.key === 'Tab' && isDesktop() ) {
				if ( e.shiftKey ) {
					if ( index === 0 ) {
						return;
					}
					e.preventDefault();
					focusPanelEnd( index - 1 );
					return;
				}

				e.preventDefault();
				focusPanelStart( index );
				return;
			}

			let newIndex = null;
			if ( e.key === 'ArrowRight' || e.key === 'ArrowDown' ) {
				newIndex = ( index + 1 ) % navBtns.length;
			} else if ( e.key === 'ArrowLeft' || e.key === 'ArrowUp' ) {
				newIndex = ( index - 1 + navBtns.length ) % navBtns.length;
			} else if ( e.key === 'Home' ) {
				newIndex = 0;
			} else if ( e.key === 'End' ) {
				newIndex = navBtns.length - 1;
			}
			if ( newIndex !== null ) {
				e.preventDefault();
				activate( newIndex );
				navBtns[ newIndex ].focus();
			}
		} );
	} );

	panels.forEach( ( panel, index ) => {
		panel.addEventListener( 'keydown', ( e ) => {
			if ( e.key !== 'Tab' || ! isDesktop() ) {
				return;
			}

			const focusables = getPanelFocusables( panel );
			if ( ! focusables.length ) {
				return;
			}

			const first = focusables[ 0 ];
			const last = focusables[ focusables.length - 1 ];
			const current = document.activeElement;

			if ( ! e.shiftKey && current === last ) {
				if ( index + 1 < navBtns.length ) {
					e.preventDefault();
					activate( index + 1 );
					navBtns[ index + 1 ].focus();
				}
				return;
			}

			if ( e.shiftKey && current === first ) {
				e.preventDefault();
				navBtns[ index ].focus();
			}
		} );
	} );

	accordionBtns.forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const index = parseInt( btn.dataset.tabIndex, 10 );
			activate( index, true );
		} );
	} );

	const activeIndex = panels.findIndex( ( p ) => p.classList.contains( 'is-active' ) );
	activate( activeIndex >= 0 ? activeIndex : 0 );

	const mq = window.matchMedia( `(min-width: ${ BREAKPOINT }px)` );
	mq.addEventListener( 'change', () => {
		const nextActive = panels.findIndex( ( p ) => p.classList.contains( 'is-active' ) );
		activate( nextActive >= 0 ? nextActive : 0 );
	} );
}
